import {
  EMPLOYEE_ACTIVITY_ROUTE,
  EMPLOYEE_ATTENDANCE_ROUTE,
  EMPLOYEE_ALLOWANCE_ROUTE,
  EMPLOYEE_CONTRACTS_ROUTE,
  EMPLOYEE_DOCUMENT_ROUTE,
  EMPLOYEE_GENERAL_ROUTE,
  EMPLOYEE_LEAVE_ROUTE,
  EMPLOYEE_OFFBOARDING_ROUTE,
  EMPLOYEE_OVERTIME_ROUTE,
  EMPLOYEE_PAYROLL_COMPONENT_ROUTE,
  EMPLOYEE_PAYROLL_ROUTE,
  EMPLOYEE_PAYROLL_SALARY_ROUTE,
  EMPLOYEE_PROMOTION_HISTORY_ROUTE,
  EMPLOYEE_SETTINGS_ROUTE,
  PROFILE_ALLOWANCE_ROUTE,
  PROFILE_ATTENDANCE_ROUTE,
  PROFILE_GENERAL_ROUTE,
  PROFILE_LEAVE_ROUTE,
  PROFILE_OVERTIME_ROUTE,
  PROFILE_PAYROLL_ROUTE,
} from '@/router/routes';
import { STORAGE_URL, FOLDER_FILE_DIR } from '@/utils/const';
import { PlatformModule } from '@/utils/enums';

/* --------------------------------- tabs ---------------------------------- */

export interface EmployeeTab {
  key: string;
  label: string;
  icon: string;
  route: string;
  /** every route name that should light this tab up */
  routes: string[];
  /** module the tab belongs to; core tabs have none */
  module?: PlatformModule;
}

/** Tabs the organization is licensed for, in display order. */
export const visibleTabs = (tabs: EmployeeTab[], modules: PlatformModule[]): EmployeeTab[] =>
  tabs.filter((tab) => !tab.module || modules.includes(tab.module));

export const EMPLOYEE_TABS: EmployeeTab[] = [
  { key: 'overview', label: 'Overview', icon: 'pi-id-card', route: EMPLOYEE_GENERAL_ROUTE, routes: [EMPLOYEE_GENERAL_ROUTE] },
  {
    key: 'pay',
    label: 'Pay',
    icon: 'pi-wallet',
    route: EMPLOYEE_PAYROLL_ROUTE,
    routes: [EMPLOYEE_PAYROLL_ROUTE, EMPLOYEE_PAYROLL_SALARY_ROUTE, EMPLOYEE_PAYROLL_COMPONENT_ROUTE, EMPLOYEE_PROMOTION_HISTORY_ROUTE, EMPLOYEE_ALLOWANCE_ROUTE],
    module: PlatformModule.payroll,
  },
  { key: 'leave', label: 'Leave', icon: 'pi-calendar-minus', route: EMPLOYEE_LEAVE_ROUTE, routes: [EMPLOYEE_LEAVE_ROUTE], module: PlatformModule.leave },
  { key: 'overtime', label: 'Overtime', icon: 'pi-clock', route: EMPLOYEE_OVERTIME_ROUTE, routes: [EMPLOYEE_OVERTIME_ROUTE], module: PlatformModule.payroll },
  { key: 'attendance', label: 'Attendance', icon: 'pi-calendar', route: EMPLOYEE_ATTENDANCE_ROUTE, routes: [EMPLOYEE_ATTENDANCE_ROUTE], module: PlatformModule.attendance },
  { key: 'contracts', label: 'Contracts', icon: 'pi-file-edit', route: EMPLOYEE_CONTRACTS_ROUTE, routes: [EMPLOYEE_CONTRACTS_ROUTE] },
  { key: 'documents', label: 'Documents', icon: 'pi-folder', route: EMPLOYEE_DOCUMENT_ROUTE, routes: [EMPLOYEE_DOCUMENT_ROUTE] },
  { key: 'access', label: 'Access', icon: 'pi-key', route: EMPLOYEE_SETTINGS_ROUTE, routes: [EMPLOYEE_SETTINGS_ROUTE] },
  { key: 'activity', label: 'Activity', icon: 'pi-history', route: EMPLOYEE_ACTIVITY_ROUTE, routes: [EMPLOYEE_ACTIVITY_ROUTE] },
  { key: 'offboarding', label: 'Offboarding', icon: 'pi-sign-out', route: EMPLOYEE_OFFBOARDING_ROUTE, routes: [EMPLOYEE_OFFBOARDING_ROUTE] },
];

/** Self-service mirror of EMPLOYEE_TABS, minus the admin-only tabs. */
export const PROFILE_TABS: EmployeeTab[] = [
  { key: 'overview', label: 'Overview', icon: 'pi-id-card', route: PROFILE_GENERAL_ROUTE, routes: [PROFILE_GENERAL_ROUTE] },
  { key: 'pay', label: 'Pay', icon: 'pi-wallet', route: PROFILE_PAYROLL_ROUTE, routes: [PROFILE_PAYROLL_ROUTE, PROFILE_ALLOWANCE_ROUTE], module: PlatformModule.payroll },
  { key: 'leave', label: 'Leave', icon: 'pi-calendar-minus', route: PROFILE_LEAVE_ROUTE, routes: [PROFILE_LEAVE_ROUTE], module: PlatformModule.leave },
  { key: 'overtime', label: 'Overtime', icon: 'pi-clock', route: PROFILE_OVERTIME_ROUTE, routes: [PROFILE_OVERTIME_ROUTE], module: PlatformModule.payroll },
  { key: 'attendance', label: 'Attendance', icon: 'pi-calendar', route: PROFILE_ATTENDANCE_ROUTE, routes: [PROFILE_ATTENDANCE_ROUTE], module: PlatformModule.attendance },
];

/* -------------------------------- people --------------------------------- */

const cap = (value: string | null | undefined): string => {
  const s = String(value ?? '').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

export const fullName = (employee: any): string => {
  if (!employee) return '';
  return `${cap(employee.fname)} ${cap(employee.lname)}`.trim();
};

export const positionText = (position: any): string => {
  if (!position) return '';
  const title = position.position?.title ?? position.position ?? '';
  const dept = position.department?.name ?? position.department ?? '';
  return [title, dept].filter(Boolean).join(' · ');
};

/** "2 yrs 3 mos" style tenure from a hire date. */
export const tenureLabel = (hireDate: string | Date | null | undefined): string => {
  if (!hireDate) return '';
  const start = new Date(hireDate);
  if (Number.isNaN(start.getTime())) return '';
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) return 'starts soon';
  if (months < 1) return 'less than a month';
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts: string[] = [];
  if (years) parts.push(`${years} yr${years === 1 ? '' : 's'}`);
  if (rest) parts.push(`${rest} mo${rest === 1 ? '' : 's'}`);
  return parts.join(' ');
};

/** Strip the country code so the phone input shows the local digits. */
export const localPhone = (phone: string | null | undefined): string => String(phone ?? '').replace(/^\+?251/, '');

export const GENDERS = ['male', 'female'];

/* -------------------------------- statuses -------------------------------- */

export const LEAVE_STATUS_META: Record<string, { label: string; pill: string }> = {
  pending: { label: 'Pending', pill: 'status-late' },
  approved: { label: 'Approved', pill: 'status-present' },
  rejected: { label: 'Rejected', pill: 'status-absent' },
  cancelled: { label: 'Cancelled', pill: 'status-off' },
};

export const leaveStatusMeta = (status: string) => LEAVE_STATUS_META[status] ?? { label: cap(status), pill: 'status-off' };

export const OFFBOARDING_STATUS_META: Record<string, { label: string; pill: string }> = {
  pending: { label: 'Pending', pill: 'status-off' },
  in_progress: { label: 'In progress', pill: 'status-late' },
  completed: { label: 'Completed', pill: 'status-present' },
  cancelled: { label: 'Cancelled', pill: 'status-absent' },
};

export const offboardingStatusMeta = (status: string) => OFFBOARDING_STATUS_META[status] ?? { label: cap(status), pill: 'status-off' };

export const OFFBOARDING_TYPE_LABELS: Record<string, string> = {
  resignation: 'Resignation',
  termination: 'Termination',
  contract_end: 'End of contract',
  retirement: 'Retirement',
  death: 'Death',
};

export const offboardingTypeLabel = (type: string) => OFFBOARDING_TYPE_LABELS[type] ?? cap(String(type ?? '').replace(/_/g, ' '));

export const CONTRACT_TYPE_LABELS: Record<string, string> = {
  'full-time': 'Full time',
  'part-time': 'Part time',
  contractual: 'Contract',
  internship: 'Internship',
};

export const contractTypeLabel = (type: string) => CONTRACT_TYPE_LABELS[type] ?? cap(type);

/** Where a contract sits relative to today. */
export const contractState = (contract: any): { key: string; label: string; pill: string } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = contract?.startDate ? new Date(contract.startDate) : null;
  const end = contract?.endDate ? new Date(contract.endDate) : null;
  if (start && start > today) return { key: 'upcoming', label: 'Starts soon', pill: 'status-leave' };
  if (end && end < today) return { key: 'ended', label: 'Ended', pill: 'status-off' };
  if (end) {
    const days = Math.ceil((end.getTime() - today.getTime()) / 86400000);
    if (days <= 30) return { key: 'ending', label: `Ends in ${days} day${days === 1 ? '' : 's'}`, pill: 'status-late' };
  }
  return { key: 'active', label: 'Active', pill: 'status-present' };
};

export const LEDGER_ENTRY_META: Record<string, { label: string; pill: string }> = {
  accrual: { label: 'Accrual', pill: 'status-present' },
  taken: { label: 'Taken', pill: 'status-absent' },
  adjustment: { label: 'Adjustment', pill: 'status-leave' },
  opening: { label: 'Opening', pill: 'status-off' },
  encashment: { label: 'Encashment', pill: 'status-late' },
  expiry: { label: 'Expiry', pill: 'status-off' },
};

export const ledgerEntryMeta = (type: string) => LEDGER_ENTRY_META[String(type ?? '').toLowerCase()] ?? { label: cap(type), pill: 'status-off' };

/* --------------------------------- files ---------------------------------- */

export const fileUrl = (file: any): string => (file?.file ? `${STORAGE_URL}/${FOLDER_FILE_DIR}/${file.file}` : '');

export const fileExtension = (name: string | null | undefined): string => {
  const s = String(name ?? '');
  const i = s.lastIndexOf('.');
  return i >= 0 ? s.slice(i + 1).toUpperCase().slice(0, 5) : 'FILE';
};

export const stripExtension = (name: string): string => {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(0, i) : name;
};

export const FILE_ICONS: Record<string, string> = {
  PDF: 'pi-file-pdf',
  DOC: 'pi-file-word',
  DOCX: 'pi-file-word',
  XLS: 'pi-file-excel',
  XLSX: 'pi-file-excel',
  CSV: 'pi-file-excel',
  PNG: 'pi-image',
  JPG: 'pi-image',
  JPEG: 'pi-image',
  GIF: 'pi-image',
};

export const fileIcon = (name: string | null | undefined): string => FILE_ICONS[fileExtension(name)] ?? 'pi-file';

/* -------------------------------- overtime -------------------------------- */

export const overtimeRated = (entry: any): number => {
  const hours = Number(entry?.hoursWorked) || 0;
  const multiplier = Number(entry?.overtimeRate?.multiplier) || 1;
  return Math.round(hours * multiplier * 100) / 100;
};

export const formatHours = (value: number): string => {
  const n = Number(value) || 0;
  return `${Number.isInteger(n) ? n : n.toFixed(1)} h`;
};

/* --------------------------------- leave ---------------------------------- */

const addMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() < day) d.setDate(0);
  return d;
};

export interface LeaveEligibility {
  eligible: boolean;
  tracked: boolean;
  /** headline figure for the card */
  value: string;
  /** secondary line */
  note: string;
}

/** Ports the balance-card rules: gender, minimum service, then what to show. */
export const leaveEligibility = (balance: any, employee: any): LeaveEligibility => {
  const policy = balance?.leaveType?.leavePolicy ?? null;
  const tracked = !!policy?.balanceTracked;
  if (!policy) return { eligible: false, tracked, value: '', note: 'Policy not configured' };

  const gender = policy.eligibilityGender;
  if (gender && gender !== 'any' && employee?.gender && gender !== employee.gender) {
    return { eligible: false, tracked, value: '', note: 'Not applicable for this employee' };
  }

  const minMonths = Number(policy.eligibilityMinServiceMonths ?? 0);
  if (minMonths > 0) {
    if (!employee?.hireDate) return { eligible: false, tracked, value: '', note: 'Needs a join date to work out eligibility' };
    const eligibleOn = addMonths(new Date(employee.hireDate), minMonths);
    if (new Date() < eligibleOn) {
      return { eligible: false, tracked, value: '', note: `Eligible from ${eligibleOn.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} after ${minMonths} months of service` };
    }
  }

  if (tracked) {
    const available = Number(balance?.available ?? 0);
    const taken = Number(balance?.totalTaken ?? 0);
    const accrued = Number(balance?.totalAccrued ?? 0) + Number(balance?.totalAdjusted ?? 0);
    return { eligible: true, tracked, value: `${available.toFixed(1)}`, note: `${taken.toFixed(1)} used of ${accrued.toFixed(1)} earned` };
  }
  if (policy.capPerYearDays) return { eligible: true, tracked, value: `${Number(policy.capPerYearDays)}`, note: 'days per year' };
  if (policy.baseDays) return { eligible: true, tracked, value: `${Number(policy.baseDays)}`, note: 'days per event' };
  return { eligible: true, tracked, value: '', note: 'Available when the event occurs' };
};

export const leaveDaysLabel = (request: any): string => {
  const days = Number(request?.days ?? 0);
  if (days === 0.5) return `Half day${request?.halfDaySlot ? ` (${String(request.halfDaySlot).toUpperCase()})` : ''}`;
  return `${Number.isInteger(days) ? days : days.toFixed(1)} day${days === 1 ? '' : 's'}`;
};
